/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { FiFilter, FiDownload, FiPackage, FiUser, FiAward, FiPieChart, FiChevronDown, FiChevronUp, FiFileText, FiTrendingUp } from "react-icons/fi";
import { Tr, Td } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";
import ResponsiveTable from "../../components/ResponsiveTable";
import { useUI } from "../../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../../hooks/useTenantStorePermissions";
import NoAccess from "../../components/NoAccess";
import { formatNumber } from "utils/formatNumber";
import * as XLSX from "xlsx";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export default function SalesReport() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const baseUrl = (
    process.env.NODE_ENV === "development"
      ? "/api"
      : (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api")
  ).replace(/\/+$/, "");
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("total");
  const [loading, setLoading] = useState(false);
  const [profitData, setProfitData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [period, setPeriod] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [filterByProduct, setFilterByProduct] = useState(false);

  const [expandedProductId, setExpandedProductId] = useState(null);
  const [variantDetails, setVariantDetails] = useState(null);
  const [loadingVariants, setLoadingVariants] = useState(false);

  // Original Sales Report States
  const [salesSummary, setSalesSummary] = useState({
    TotalAmount: 0,
    TotalNetAmount: 0,
    Totalprice: 0,
    TotalQuantity: 0,
  });
  const [orders, setOrders] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);

  const tabs = [
    {
      id: "total",
      label: isArabic ? "1- الأرباح العامة" : "1- General Profits",
      icon: <FiPieChart />,
      endpoint: "/ProfitReports/total",
    },
    {
      id: "product",
      label: isArabic ? "2- أرباح منتج معين" : "2- Profit by Product",
      icon: <FiPackage />,
      endpoint: "/ProfitReports/product",
    },
    {
      id: "customer",
      label: isArabic ? "3- أرباح عميل معين" : "3- Profit by Customer",
      icon: <FiUser />,
      endpoint: "/ProfitReports/customer",
    },
    {
      id: "brand",
      label: isArabic ? "4- أرباح براند معين" : "4- Profit by Brand",
      icon: <FiAward />,
      endpoint: "/ProfitReports/brand",
    },
  ];

  const extractPeriod = (dateStr) => {
    if (!dateStr) return "";
    try {
      const fixed = dateStr.replace(/\s+/g, " ").replace("AM", " AM").replace("PM", " PM").trim();
      const d = new Date(Date.parse(fixed));
      return isNaN(d) ? "" : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    } catch (e) { return ""; }
  };

  const fetchData = async () => {
    if (!perms.viewSalesReport || !tenantId) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        "Accept": "*/*", // More permissive as seen in Swagger
        "Authorization": token ? `Bearer ${token}` : "",
      };

      // 1. Always Fetch Grand Total Summary
      const totalRes = await fetch(`${baseUrl}/ProfitReports/total?tenantId=${tenantId}`, { headers });
      if (totalRes.ok) {
        const totalResult = await totalRes.json();
        setGrandTotal(totalResult);
      }

      // 2. Fetch Profit Data if tab selected
      if (activeTab) {
        const currentTab = tabs.find(t => t.id === activeTab);
        let endpointUrl = `${baseUrl}${currentTab.endpoint}?tenantId=${tenantId}`;

        // Add required parameters based on Swagger specs
        if (activeTab === "product") endpointUrl += "&itemId=-1";
        else if (activeTab === "customer") endpointUrl += "&customerId=-1";
        else if (activeTab === "brand") endpointUrl += "&tagId=-1";

        console.log(`🔍 Fetching Profit Data from: ${endpointUrl}`);

        const profitRes = await fetch(endpointUrl, { headers });
        if (profitRes.ok) {
          const result = await profitRes.json();
          console.log(`✅ Profit Data Received (${activeTab}):`, result);

          if (activeTab === "total") {
            setSummaryData(result);
            setProfitData([]);
          } else {
            setProfitData(Array.isArray(result) ? result : []);
            setSummaryData(null);
          }
        } else {
          console.error(`❌ Profit API Error: ${profitRes.status}`);
        }
      } else {
        // 3. Fetch Original Sales Data only if NO profit tab is active
        const salesRes = await fetch(`${baseUrl}/TenantReports/Sales?tenantId=${tenantId}`, { headers });
        if (salesRes.ok) {
          const data = await salesRes.json();
          if (Array.isArray(data) && data.length > 0) setSalesSummary(data[0]);
          else if (data && typeof data === 'object') setSalesSummary(data);
        }

        const ordersRes = await fetch(`${baseUrl}/GetTenantOrders?tenantId=${tenantId}&statusId=-1&OrderNo=-1`, { headers });
        if (ordersRes.ok) {
          const ordersData = await ordersRes.json();
          if (Array.isArray(ordersData)) {
            const mappedOrders = ordersData.map(order => ({
              id: order.orderId,
              buyerName: order.customerName || (isArabic ? "عميل" : "Customer"),
              orderValue: Number(order.totalAmount || 0),
              orderDate: order.orderDate,
              location: order.city || "—",
              paymentMethod: order.paymentMethodName || "—",
              orderStatus: order.status,
              isPaid: Number(order.isPaid || 0),
              details: order.details || []
            }));
            setOrders(mappedOrders);

            const productMap = new Map();
            mappedOrders.forEach(order => {
              const items = order.details || [];
              items.forEach(item => {
                const productName = item.itemName?.trim() || `#${item.itemId}`;
                const quantity = Number(item.quantity || 0);
                const price = Number(item.lineTotal || item.price || 0);
                const isCompleted = Number(order.isPaid) === 1;

                if (!productMap.has(productName)) {
                  productMap.set(productName, {
                    id: productMap.size + 1,
                    period: extractPeriod(order.orderDate),
                    productName,
                    ordersCount: 1,
                    sellingPrice: price,
                    completedOrders: isCompleted ? 1 : 0,
                    incompleteOrders: isCompleted ? 0 : 1,
                    totalSalesValue: price * quantity,
                    totalQuantity: quantity,
                  });
                } else {
                  const existing = productMap.get(productName);
                  existing.ordersCount += 1;
                  existing.totalQuantity += quantity;
                  existing.totalSalesValue += price * quantity;
                  if (isCompleted) existing.completedOrders += 1;
                  else existing.incompleteOrders += 1;
                }
              });
            });
            setSalesData(Array.from(productMap.values()));
          }
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpandProduct = async (itemId) => {
    if (expandedProductId === itemId) {
      setExpandedProductId(null);
      return;
    }
    setExpandedProductId(itemId);
    setLoadingVariants(true);
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        "Accept": "*/*",
        "Authorization": token ? `Bearer ${token}` : "",
      };
      const res = await fetch(`${baseUrl}/GetItems?tenantId=${tenantId}&itemId=${itemId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) {
          setVariantDetails(data[0].variants || []);
        } else {
          setVariantDetails([]);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingVariants(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, tenantId, perms.viewSalesReport]);

  // Combined Searching logic with safety for case sensitivity
  const filteredProfitData = useMemo(() => {
    let result = profitData || [];
    if (productFilter) {
      const query = productFilter.toLowerCase();
      result = result.filter(item => {
        const name = (item.ItemName || item.itemName || item.CustomerName || item.customerName || item.TagName || item.tagName || "").toLowerCase();
        return name.includes(query);
      });
    }
    return result;
  }, [profitData, productFilter, activeTab]);

  const filteredSalesData = useMemo(() => {
    let result = salesData || [];
    if (period) result = result.filter(item => item.period === period);
    if (productFilter && !activeTab) {
      result = result.filter(item => item.productName?.toLowerCase().includes(productFilter.toLowerCase()));
    }
    return result;
  }, [salesData, period, productFilter, activeTab]);

  const exportToExcel = () => {
    let dataToExport = [];
    if (activeTab === "total") {
      dataToExport = [{
        [isArabic ? "إجمالي المبيعات" : "Total Sales"]: getValue(summaryData || grandTotal || {}, 'TotalSales'),
        [isArabic ? "إجمالي التكلفة" : "Total Cost"]: getValue(summaryData || grandTotal || {}, 'TotalCost'),
        [isArabic ? "صافي الربح" : "Total Profit"]: getValue(summaryData || grandTotal || {}, 'TotalProfit')
      }];
    } else if (activeTab === "product") {
      dataToExport = filteredProfitData.map(row => ({
        [isArabic ? "اسم المنتج" : "Product Name"]: row.ItemName || row.itemName,
        [isArabic ? "سعر البيع" : "Sale Price"]: row.Price || row.price,
        [isArabic ? "سعر الكلفة" : "Cost Price"]: row.CostPrice || row.costPrice,
        [isArabic ? "الكمية الكلية" : "Total Qty"]: row.TotalQty || row.totalQty,
        [isArabic ? "إجمالي الربح" : "Total Profit"]: row.TotalProfit || row.totalProfit,
        [isArabic ? "صافي الربح" : "Net Profit"]: row.NetProfit || row.netProfit,
      }));
    } else if (activeTab === "customer") {
      dataToExport = filteredProfitData.map(row => ({
        [isArabic ? "اسم العميل" : "Customer Name"]: row.CustomerName || row.customerName,
        [isArabic ? "إجمالي الربح" : "Total Profit"]: row.TotalProfit || row.totalProfit,
      }));
    } else if (activeTab === "brand") {
      dataToExport = filteredProfitData.map(row => ({
        [isArabic ? "اسم البراند" : "Brand Name"]: row.TagName || row.tagName,
        [isArabic ? "إجمالي الربح" : "Total Profit"]: row.TotalProfit || row.totalProfit,
      }));
    } else {
      dataToExport = filteredSalesData;
    }

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `Report_${activeTab || 'Sales'}.xlsx`);
  };

  if (!perms.viewSalesReport) return <NoAccess />;

  const safeSummary = {
    TotalAmount: Math.round(Number(salesSummary.TotalAmount || salesSummary.totalAmount || 0)),
    TotalNetAmount: Math.round(Number(salesSummary.TotalNetAmount || salesSummary.totalNetAmount || 0)),
    Totalprice: Math.round(Number(salesSummary.Totalprice || salesSummary.totalPrice || 0)),
    TotalQuantity: Number(salesSummary.TotalQuantity || salesSummary.totalQuantity || 0),
  };

  // Helper for Pascal/Camel Case safety
  const getValue = (obj, key) => obj[key] || obj[key.charAt(0).toLowerCase() + key.slice(1)] || 0;

  return (
    <PageContainer title={isArabic ? "تقرير المبيعات والارباح" : "Sales & Profit Report"}>
      {/* Grand Summary Section - Always Visible */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <UnifiedCard hoverable={false} className="overflow-hidden border-l-4" style={{ borderColor: colors.mainColor }}>
          <div className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold opacity-60 uppercase mb-1">{isArabic ? "إجمالي المبيعات" : "Total Sales"}</p>
              <h4 className="text-2xl font-black" style={{ color: colors.mainColor }}>{formatNumber(getValue(grandTotal || {}, 'TotalSales'))} <span className="text-xs font-normal">IQD</span></h4>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.mainColor}15`, color: colors.mainColor }}>
              <FiTrendingUp size={24} />
            </div>
          </div>
        </UnifiedCard>
        <UnifiedCard hoverable={false} className="overflow-hidden border-l-4" style={{ borderColor: colors.red }}>
          <div className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold opacity-60 uppercase mb-1">{isArabic ? "إجمالي التكلفة" : "Total Cost"}</p>
              <h4 className="text-2xl font-black" style={{ color: colors.red }}>{formatNumber(getValue(grandTotal || {}, 'TotalCost'))} <span className="text-xs font-normal">IQD</span></h4>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.red}15`, color: colors.red }}>
              <FiPieChart size={24} />
            </div>
          </div>
        </UnifiedCard>
        <UnifiedCard hoverable={false} className="overflow-hidden border-l-4" style={{ borderColor: colors.green }}>
          <div className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold opacity-60 uppercase mb-1">{isArabic ? "صافي الربح" : "Total Profit"}</p>
              <h4 className="text-2xl font-black" style={{ color: colors.green }}>{formatNumber(getValue(grandTotal || {}, 'TotalProfit'))} <span className="text-xs font-normal">IQD</span></h4>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.green}15`, color: colors.green }}>
              <FiAward size={24} />
            </div>
          </div>
        </UnifiedCard>
      </motion.div>

      {/* Profit Tabs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {tabs.map((tab) => (
          <motion.div key={tab.id} whileHover={{ y: -5 }} whileTap={{ scale: 0.98 }} onClick={() => setActiveTab(activeTab === tab.id ? undefined : tab.id)} className="cursor-pointer">
            <UnifiedCard className={`transition-all duration-300 ${activeTab === tab.id ? 'ring-2 ring-offset-2' : ''}`}
              style={{
                borderColor: activeTab === tab.id ? colors.mainColor : 'transparent',
                backgroundColor: activeTab === tab.id ? (isDark ? 'rgba(116, 0, 191, 0.1)' : 'rgba(116, 0, 191, 0.05)') : (isDark ? 'rgba(30, 41, 59, 0.5)' : '#fff'),
                ringColor: colors.mainColor
              }}>
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: activeTab === tab.id ? colors.mainColor : (isDark ? '#334155' : '#f1f5f9'), color: activeTab === tab.id ? '#fff' : colors.mainColor }}>
                  {tab.icon}
                </div>
                <h3 className={`text-lg font-bold ${activeTab === tab.id ? (isDark ? 'text-white' : 'text-[#7400BF]') : (isDark ? 'text-slate-200' : 'text-slate-700')}`}>
                  {tab.label}
                </h3>
              </div>
            </UnifiedCard>
          </motion.div>
        ))}
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 border-4 border-t-transparent animate-spin rounded-full" style={{ borderColor: colors.mainColor, borderTopColor: 'transparent' }}></div>
        </div>
      )}

      {/* Tables & Views */}
      {!loading && (
        <>
          {/* Summary Cards */}
          {!activeTab && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <UnifiedCard hoverable={false}><div className="text-center"><p className="text-sm opacity-70 mb-1">{isArabic ? "إجمالي المبلغ" : "Total Amount"}</p><p className="text-2xl font-bold" style={{ color: colors.mainColor }}>{formatNumber(safeSummary.TotalAmount)} {isArabic ? "د.ع" : "IRQ"}</p></div></UnifiedCard>
                <UnifiedCard hoverable={false}><div className="text-center"><p className="text-sm opacity-70 mb-1">{isArabic ? "صافي المبلغ" : "Net Amount"}</p><p className="text-2xl font-bold" style={{ color: colors.green }}>{formatNumber(safeSummary.TotalNetAmount)} {isArabic ? "د.ع" : "IRQ"}</p></div></UnifiedCard>
                <UnifiedCard hoverable={false}><div className="text-center"><p className="text-sm opacity-70 mb-1">{isArabic ? "إجمالي السعر" : "Total Price"}</p><p className="text-2xl font-bold" style={{ color: colors.blue }}>{formatNumber(safeSummary.Totalprice)} {isArabic ? "د.ع" : "IRQ"}</p></div></UnifiedCard>
                <UnifiedCard hoverable={false}><div className="text-center"><p className="text-sm opacity-70 mb-1">{isArabic ? "إجمالي الكمية" : "Total Quantity"}</p><p className="text-2xl font-bold" style={{ color: colors.gold }}>{safeSummary.TotalQuantity}</p></div></UnifiedCard>
              </div>
            </motion.div>
          )}

          {/* Filters */}
          <UnifiedCard hoverable={false} className="mb-8">
            <div className="flex flex-wrap items-end gap-4 p-2">
              {!activeTab && (
                <div className="flex-1 min-w-[200px]">
                  <label className="block mb-2 text-sm font-bold">{isArabic ? "الفترة" : "Period"}</label>
                  <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-full px-4 py-2 rounded-lg border focus:ring-2 outline-none" style={{ backgroundColor: isDark ? "#1e293b" : "#fff", borderColor: isDark ? "#334155" : "#e2e8f0", color: isDark ? "#fff" : "#000" }} />
                </div>
              )}
              <div className="flex-1 min-w-[200px]">
                <label className="block mb-2 text-sm font-bold">{isArabic ? "البحث" : "Search"}</label>
                <input type="text" placeholder={isArabic ? "ابحث هنا..." : "Search here..."} value={productFilter} onChange={(e) => setProductFilter(e.target.value)} className="w-full px-4 py-2 rounded-lg border focus:ring-2 outline-none" style={{ backgroundColor: isDark ? "#1e293b" : "#fff", borderColor: isDark ? "#334155" : "#e2e8f0", color: isDark ? "#fff" : "#000" }} />
              </div>
              <button onClick={exportToExcel} className="flex items-center gap-2 px-6 py-2 rounded-lg font-bold text-white transition-opacity shadow-md" style={{ backgroundColor: colors.mainColor }}>
                <FiDownload /> {isArabic ? "تنزيل إكسل" : "Excel Download"}
              </button>
            </div>
          </UnifiedCard>

          {/* Dynamic Content */}
          <motion.div key={activeTab || 'sales'} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {activeTab === "total" ? (
              <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6">
                <UnifiedCard>
                  <div className="p-6">
                    <h3 className="text-lg font-bold mb-6" style={{ color: colors.mainColor }}>
                      {isArabic ? "تحليل الأرباح والخسائر" : "Profit & Loss Analysis"}
                    </h3>
                    <div className="h-[350px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={[
                            { name: isArabic ? "المبيعات" : "Sales", value: getValue(grandTotal || {}, 'TotalSales'), color: colors.mainColor },
                            { name: isArabic ? "التكلفة" : "Cost", value: getValue(grandTotal || {}, 'TotalCost'), color: colors.red },
                            { name: isArabic ? "الربح" : "Profit", value: getValue(grandTotal || {}, 'TotalProfit'), color: colors.green },
                          ]}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"} />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: isDark ? "#94a3b8" : "#64748b" }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: isDark ? "#94a3b8" : "#64748b" }} tickFormatter={(val) => `${(val / 1000).toLocaleString()}k`} />
                          <Tooltip
                            contentStyle={{ backgroundColor: isDark ? "#1e293b" : "#fff", border: "none", borderRadius: "12px", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.2)", color: isDark ? "#fff" : "#000" }}
                            formatter={(value) => [formatNumber(value) + " IQD", ""]}
                            cursor={{ fill: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)" }}
                          />
                          <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={50}>
                            {[0, 1, 2].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={[colors.mainColor, colors.red, colors.green][index]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </UnifiedCard>
              </motion.div>
            ) : activeTab ? (
              <ResponsiveTable
                columns={
                  activeTab === "product"
                    ? [
                      { header: isArabic ? "اسم المنتج" : "Product Name", sortKey: "ItemName" },
                      { header: isArabic ? "سعر البيع" : "Sale Price", sortKey: "Price", align: "center" },
                      { header: isArabic ? "سعر الكلفة" : "Cost Price", sortKey: "CostPrice", align: "center" },
                      { header: isArabic ? "الكمية الكلية" : "Total Qty", sortKey: "TotalQty", align: "center" },
                      { header: isArabic ? "إجمالي الربح" : "Total Profit", sortKey: "TotalProfit", align: "center" },
                      { header: isArabic ? "صافي الربح" : "Net Profit", sortKey: "NetProfit", align: "center" },
                      { header: isArabic ? "التفاصيل" : "Details", sortKey: null, align: "center" }
                    ]
                    : activeTab === "customer"
                      ? [
                        { header: isArabic ? "اسم العميل" : "Customer Name", sortKey: "CustomerName" },
                        { header: isArabic ? "إجمالي الربح" : "Total Profit", sortKey: "TotalProfit", align: "center" },
                        { header: isArabic ? "التفاصيل" : "Details", sortKey: null, align: "center" }
                      ]
                      : [{ header: isArabic ? "اسم البراند" : "Brand Name", sortKey: "TagName" }, { header: isArabic ? "إجمالي الربح" : "Total Profit", sortKey: "TotalProfit", align: "center" }]
                }
                data={filteredProfitData}
                renderRow={(row) => (
                  <>
                    {activeTab === "product" && (
                      <>
                        <td className="px-6 py-4 font-medium">{row.ItemName || row.itemName}</td>
                        <td className="px-6 py-4 text-center">{formatNumber(row.Price || row.price)} {isArabic ? "د.ع" : "IQD"}</td>
                        <td className="px-6 py-4 text-center">{formatNumber(row.CostPrice || row.costPrice)} {isArabic ? "د.ع" : "IQD"}</td>
                        <td className="px-6 py-4 text-center">{row.TotalQty || row.totalQty}</td>
                        <td className="px-6 py-4 text-center font-bold" style={{ color: colors.blue }}>{formatNumber(row.TotalProfit || row.totalProfit)} {isArabic ? "د.ع" : "IQD"}</td>
                        <td className="px-6 py-4 text-center font-bold">
                          <span
                            dir="ltr"
                            className="px-2 py-1 rounded text-sm"
                            style={{
                              backgroundColor: (row.NetProfit || row.netProfit || 0) >= 0 ? `${colors.green}15` : `${colors.red}15`,
                              color: (row.NetProfit || row.netProfit || 0) >= 0 ? colors.green : colors.red
                            }}
                          >
                            {formatNumber(row.NetProfit || row.netProfit)} {isArabic ? "د.ع" : "IQD"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button onClick={() => handleExpandProduct(row.ItemId || row.itemId)} className="flex items-center justify-center gap-1 mx-auto text-sm font-bold transition-all px-3 py-1 rounded hover:opacity-80 border" style={{ color: colors.mainColor, backgroundColor: isDark ? "rgba(116, 0, 191, 0.1)" : "rgba(116, 0, 191, 0.05)", borderColor: isDark ? "rgba(116, 0, 191, 0.2)" : "rgba(116, 0, 191, 0.1)" }}>
                            {isArabic ? "عرض التفاصيل" : "Details"}
                            {expandedProductId === (row.ItemId || row.itemId) ? <FiChevronUp /> : <FiChevronDown />}
                          </button>
                        </td>
                      </>
                    )}
                    {activeTab === "customer" && (
                      <>
                        <td className="px-6 py-4 font-medium">{row.CustomerName || row.customerName}</td>
                        <td className="px-6 py-4 text-center font-bold">
                          <span
                            dir="ltr"
                            className="px-2 py-1 rounded text-sm"
                            style={{
                              backgroundColor: (row.TotalProfit || row.totalProfit || 0) >= 0 ? `${colors.green}15` : `${colors.red}15`,
                              color: (row.TotalProfit || row.totalProfit || 0) >= 0 ? colors.green : colors.red
                            }}
                          >
                            {formatNumber(row.TotalProfit || row.totalProfit)} {isArabic ? "د.ع" : "IQD"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => navigate(`/admin-store/customers/details/${encodeURIComponent(row.CustomerName || row.customerName)}`, {
                              state: {
                                customerId: row.CustomerId ?? row.customerId ?? null,
                                totalProfit: Number(row.TotalProfit ?? row.totalProfit ?? 0)
                              }
                            })}
                            className="p-2 rounded-lg text-white transition-all shadow-sm mx-auto flex items-center justify-center"
                            style={{ background: colors.blue }}
                            title={isArabic ? "تفاصيل حساب العميل" : "Customer Details"}
                          >
                            <FiFileText size={16} />
                          </button>
                        </td>
                      </>
                    )}
                    {activeTab === "brand" && (
                      <>
                        <td className="px-6 py-4 font-medium">{row.TagName || row.tagName}</td>
                        <td className="px-6 py-4 text-center font-bold">
                          <span
                            dir="ltr"
                            className="px-2 py-1 rounded text-sm"
                            style={{
                              backgroundColor: (row.TotalProfit || row.totalProfit || 0) >= 0 ? `${colors.green}15` : `${colors.red}15`,
                              color: (row.TotalProfit || row.totalProfit || 0) >= 0 ? colors.green : colors.red
                            }}
                          >
                            {formatNumber(row.TotalProfit || row.totalProfit)} {isArabic ? "د.ع" : "IQD"}
                          </span>
                        </td>
                      </>
                    )}
                  </>
                )}
                renderExpandedRow={(row) => {
                  const itemId = row.ItemId || row.itemId;
                  if (activeTab === "product" && expandedProductId === itemId) {
                    return (
                      <Tr bg={isDark ? "rgba(0,0,0,0.2)" : "#fafafa"}>
                        <Td colSpan={7} p={0} borderBottom="1px solid" borderColor={isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}>
                          {loadingVariants ? (
                            <div className="flex justify-center p-6"><div className="w-6 h-6 border-2 border-t-transparent animate-spin rounded-full" style={{ borderColor: colors.mainColor, borderTopColor: 'transparent' }}></div></div>
                          ) : variantDetails && variantDetails.length > 0 ? (
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg m-4 shadow-inner border border-slate-200 dark:border-slate-700">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                                    <th className="py-2 px-4 text-start font-bold">{isArabic ? "اسم المتغير" : "Variant Name"}</th>
                                    <th className="py-2 px-4 text-start font-bold">{isArabic ? "اللون" : "Color"}</th>
                                    <th className="py-2 px-4 text-center font-bold">{isArabic ? "سعر البيع" : "Sale Price"}</th>
                                    <th className="py-2 px-4 text-center font-bold">{isArabic ? "سعر التكلفة" : "Cost Price"}</th>
                                    <th className="py-2 px-4 text-center font-bold">{isArabic ? "صافي الربح" : "Profit"}</th>
                                    <th className="py-2 px-4 text-start font-bold">{isArabic ? "القياسات والمخزون" : "Sizes & Stock"}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {variantDetails.map((v, index) => (
                                    <tr key={v.variantId || index} className="border-b last:border-0 border-slate-100 dark:border-slate-700/50">
                                      <td className="py-2 px-4 font-medium">{v.variantName || '—'}</td>
                                      <td className="py-2 px-4">
                                        <div className="flex items-center gap-2">
                                          {v.color?.hexCode && (
                                            <span className="w-4 h-4 rounded-full border shadow-sm block" style={{ backgroundColor: v.color.hexCode }}></span>
                                          )}
                                          <span>{v.color?.name || '—'}</span>
                                        </div>
                                      </td>
                                      <td className="py-2 px-4 text-center">{formatNumber(v.price)} <span className="text-[10px] opacity-70">IQD</span></td>
                                      <td className="py-2 px-4 text-center">{formatNumber(v.costPrice)} <span className="text-[10px] opacity-70">IQD</span></td>
                                      <td className="py-2 px-4 text-center font-bold" style={{ color: colors.green }}>{formatNumber((v.price || 0) - (v.costPrice || 0))} <span className="text-[10px] opacity-70">IQD</span></td>
                                      <td className="py-2 px-4">
                                        <div className="flex flex-wrap gap-1">
                                          {v.sizes?.map((s, i) => (
                                            <span key={i} className="inline-block px-2 py-1 rounded bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-xs shadow-sm" dir="ltr">
                                              <span className="font-bold opacity-70 mr-1">{s.size}:</span>
                                              <span className={s.stockQty > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>{s.stockQty}</span>
                                            </span>
                                          )) || '—'}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="p-6 text-center text-sm opacity-60">
                              {isArabic ? "لا توجد متغيرات لهذا المنتج" : "No variants found for this product"}
                            </div>
                          )}
                        </Td>
                      </Tr>
                    );
                  }
                  return null;
                }}
              />
            ) : (
              <ResponsiveTable
                columns={[{ header: isArabic ? "الفترة" : "Period", sortKey: "period", align: "center" }, { header: isArabic ? "اسم المنتج" : "Product Name", sortKey: "productName", align: "center" }, { header: isArabic ? "عدد الطلبات" : "Orders", sortKey: "ordersCount", align: "center" }, { header: isArabic ? "سعر البيع" : "Price", sortKey: "sellingPrice", align: "center" }, { header: isArabic ? "الكمية" : "Qty", sortKey: "totalQuantity", align: "center" }, { header: isArabic ? "القيمة الكلية" : "Total", sortKey: "totalSalesValue", align: "center" }]}
                data={filteredSalesData}
                renderRow={(row) => (
                  <><td className="py-3 px-4 text-center">{row.period || "—"}</td><td className="py-3 px-4 text-center font-medium">{row.productName || "—"}</td><td className="py-3 px-4 text-center">{row.ordersCount || 0}</td><td className="py-3 px-4 text-center">{formatNumber(row.sellingPrice || 0)}</td><td className="py-3 px-4 text-center font-bold">{row.totalQuantity || 0}</td><td className="py-3 px-4 text-center font-bold" style={{ color: colors.green }}>{formatNumber(row.totalSalesValue || 0)}</td></>
                )}
              />
            )}
          </motion.div>
        </>
      )}
    </PageContainer>
  );
}
